/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { LocationModel, WorkflowItemType } from '@/models/record/record-base.model';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ButtonAction,
  ButtonType,
  ConfirmDeleteDialogData,
  ConfirmDialogService
} from '@epg/ng-components';
import {
  faArrowRight,
  faArrowRotateRight,
  faCheck,
  faClipboardCheck,
  faCode,
  faCopy,
  faExclamationTriangle,
  faFilter,
  faPencilAlt,
  faSearchPlus,
  faSpinnerThird,
  faStar,
  faTrash,
  IconDefinition
} from '@fortawesome/pro-regular-svg-icons';
import { Subscription } from 'rxjs';
import { toTitleCase } from '../../../../../app/shared/functions';
import { HttpParams } from '@angular/common/http';
import { TaskAssignmentType } from '../../../../../enums/task-assignment-type.enum';
import { TaskStatus } from '../../../../../enums/task-status.enum';
import { TaskDatumType, TaskModel } from '../../../../../models/task.model';
import { EmployeeService } from '../../../../../services/employee.service';
import { TaskService } from '../../../../../services/task.service';
import {
  OneAppActionsMenuConfig,
  OneAppActionsMenuItemConfig,
  OneAppButtonConfig
} from '../../../../shared/oneapp-header/oneapp-header.component';

const REFRESH_CALL_BACK_IDENTIFIER = 'refresh';
const DELETE_CALL_BACK_IDENTIFIER = 'delete';
const CLOSE_TASK_CALL_BACK_IDENTIFIER = 'close_task';
const DISMISS_CALL_BACK_IDENTIFIER = 'dismiss';
@Component({
  selector: 'app-task-detail',
  templateUrl: './task-detail.component.html',
  styleUrls: ['./task-detail.component.scss'],
  standalone: false
})
export class TaskDetailComponent implements OnInit, OnDestroy, OnChanges {
  @Input() taskId!: string;
  @Input() showDismissButton: boolean = false;
  @Input() enableLink: boolean = true;

  @Output() onDismissCallback = new EventEmitter<void>();

  tasksChangedSubscription: Subscription = new Subscription();
  taskChangedSubscription: Subscription = new Subscription();
  routeSub: Subscription = new Subscription();

  faEdit = faPencilAlt;
  faCode = faCode;
  faSearchPlus = faSearchPlus;
  faSpinnerThird = faSpinnerThird;
  faTrash = faTrash;
  faFilter = faFilter;
  faExclamationTriangle = faExclamationTriangle;
  faArrowRotateRight = faArrowRotateRight;
  faStar = faStar;
  faArrowRight = faArrowRight;
  faCheck = faCheck;
  faClipboardCheck = faClipboardCheck;
  faCopy = faCopy;

  TaskStatus = TaskStatus;
  task!: TaskModel;
  taskForm!: FormGroup;
  currentTask!: TaskModel;
  outputCodeVisible = false;
  selectedTabIndex = 0;

  TaskAssignmentType = TaskAssignmentType;
  TaskDatumType = TaskDatumType;

  private _loadingTask = false;

  taskStatesGeotags: TaskStateLocationData[] = [];

  get loadingTask(): boolean {
    return this._loadingTask;
  }

  set loadingTask(value: boolean) {
    this.setTaskLoadingStatus(value);
  }

  buttonAction = ButtonAction;
  buttonType = ButtonType;

  customButtonConfig: OneAppButtonConfig = {
    id: 'refresh_task',
    text: $localize`:refresh task header@@refreshTask:refresh`,
    disabled: this.loadingTask,
    callBackIdentifier: REFRESH_CALL_BACK_IDENTIFIER,
    icon: faArrowRotateRight,
    requiredPermissions: []
  };

  dismissButtonConfig: OneAppButtonConfig = {
    id: 'close_task',
    callBackIdentifier: DISMISS_CALL_BACK_IDENTIFIER,
    disabled: false,
    requiredPermissions: []
  };

  actionsMenu: OneAppActionsMenuItemConfig[] = [
    {
      id: 'close_task_action',
      text: $localize`:close task@@closeTask:Close task`,
      callBackIdentifier: CLOSE_TASK_CALL_BACK_IDENTIFIER,
      disabled: false,
      icon: faClipboardCheck
    },
    {
      id: 'delete_task_action',
      text: $localize`:@@deleteTask:Delete task`,
      callBackIdentifier: DELETE_CALL_BACK_IDENTIFIER,
      disabled: false,
      icon: faTrash,
      color: '#DE2839'
    }
  ];

  actionsMenuConfig: OneAppActionsMenuConfig = {
    id: 'task_detail_menu',
    disabled: this.loadingTask,
    actionsMenu: this.actionsMenu
  };

  constructor(
    private route: ActivatedRoute,
    private taskService: TaskService,
    private employeeService: EmployeeService,
    private cdr: ChangeDetectorRef,
    private readonly dialogService: ConfirmDialogService
  ) {}

  ngAfterViewInit() {
    this.customButtonConfig.disabled = this.loadingTask;
    this.actionsMenuConfig.disabled = this.loadingTask;
    this.updateActionsMenuState();
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.taskStatesGeotags = [];
    this.taskChangedSubscription = this.taskService.taskChanged.subscribe((task) => {
      if (task && this.taskId && this.taskId == task.taskId) {
        this.readTaskStatesGeotags(task);
        this.task = task;
        this.loadingTask = false;
        this.updateActionsMenuState();
        this.cdr.detectChanges();
        this.updateSelectedIndex();
      }
    });
    this.routeSub = this.route.params.subscribe((params) => {
      if (params.taskId) {
        this.loadingTask = true;
        this.taskId = params.taskId;
        this.taskService.getTask(this.taskId, new HttpParams().append('format', 'FULL'));
      }
    });
    this.employeeService.getEmployees();
    this.employeeService.getEmployeeGroups();
    if (this.taskId) {
      this.taskService.getTask(this.taskId, new HttpParams().append('format', 'FULL'));
    }
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['task']) {
      this.cdr.detectChanges(); // Trigger change detection when task changes
    }

    if (changes['taskId'] && changes['taskId'].currentValue) {
      this.loadingTask = true;
      this.cdr.detectChanges();
      this.task = await this.taskService.getTask(
        changes['taskId'].currentValue,
        new HttpParams().append('format', 'FULL')
      );
      this.loadingTask = false;
    }

    this.updateSelectedIndex();
  }

  ngOnDestroy(): void {
    this.taskChangedSubscription.unsubscribe();
    this.tasksChangedSubscription.unsubscribe();
    this.routeSub.unsubscribe();
  }

  updateSelectedIndex() {
    if (!this.task) return;
    const protocolEnabled = this.hasProtocolTabContent();
    const outputEnabled = this.task.status !== TaskStatus.NEW && !!this.task.output;
    const attachmentsEnabled = !!this.task.meta_data?.attachments?.length;
    const maxTabIndex = attachmentsEnabled ? 3 : 2;

    let isCurrentTabValid = this.selectedTabIndex >= 0 && this.selectedTabIndex <= maxTabIndex;

    if (isCurrentTabValid) {
      if (this.selectedTabIndex === 1 && !protocolEnabled) {
        isCurrentTabValid = false;
      }

      if (this.selectedTabIndex === 2 && !outputEnabled) {
        isCurrentTabValid = false;
      }

      if (this.selectedTabIndex === 3 && !attachmentsEnabled) {
        isCurrentTabValid = false;
      }
    }

    if (!isCurrentTabValid) {
      this.selectedTabIndex = 0;
    }
  }

  hasMapContent(): boolean {
    if (!this.task || this.task.status === TaskStatus.NEW) {
      return false;
    }

    return !!this.task.tracing?.last_known_location || this.taskStatesGeotags.length > 0;
  }

  hasProtocolTabContent(): boolean {
    if (!this.task || this.task.status === TaskStatus.NEW) {
      return false;
    }

    return !!this.task.record || this.hasMapContent();
  }

  isClosedBeforeFinished(task: TaskModel | undefined): boolean {
    return !!task && task.status === TaskStatus.CLOSED && task.currentState > 0;
  }

  setTaskLoadingStatus(status: boolean) {
    this._loadingTask = status;
    this.customButtonConfig.disabled = status;
    this.actionsMenuConfig.disabled = status;
    this.updateActionsMenuState();
    this.cdr.detectChanges();
  }

  private updateActionsMenuState(): void {
    const closeAction = this.actionsMenu.find(
      (action) => action.callBackIdentifier === CLOSE_TASK_CALL_BACK_IDENTIFIER
    );
    const deleteAction = this.actionsMenu.find(
      (action) => action.callBackIdentifier === DELETE_CALL_BACK_IDENTIFIER
    );

    if (closeAction) {
      closeAction.disabled = this.loadingTask || this.task?.status === TaskStatus.CLOSED;
    }

    if (deleteAction) {
      deleteAction.disabled = this.loadingTask;
    }
  }

  getTaskDatumType(index: any) {
    return TaskDatumType[index as number];
  }

  readTaskStatesGeotags(task: TaskModel): void {
    this.taskStatesGeotags = [];
    if (!task.record || task.record?.length <= 0) return;
    this.taskStatesGeotags = task.record
      ?.filter((entry) => entry.geolocation)
      .map(
        (entry) =>
          new TaskStateLocationData(
            entry.itemType,
            entry.itemTitle,
            entry.itemIcon,
            entry.geolocation,
            entry.timestamp
          )
      );
  }

  deleteTask(task: TaskModel) {
    const title = $localize`:delete task header@@deleteTask:Delete Task`;
    const msg = $localize`:delete confirmation text Task@@deleteConfirmationTask:Do you really want to delete this task?`;
    const confirmButtonText = $localize`:button text delete@@buttonDelete:DELETE`;
    const cancelButtonText = $localize`:button text cancel@@buttonCancel:CANCEL`;
    const dialogData: ConfirmDeleteDialogData = {
      itemName: task.name,
      title: title,
      message: msg,
      confirmText: toTitleCase(confirmButtonText),
      cancelText: toTitleCase(cancelButtonText)
    };
    this.dialogService.openConfirmDelete(dialogData).subscribe((result: boolean) => {
      if (result == true) {
        this.taskService.deleteTask(task.taskId);
      }
    });
  }

  refreshTask() {
    this.loadingTask = true;
    this.taskService.getTask(this.taskId, new HttpParams().append('format', 'FULL'));
  }

  confirmCloseTask(task: TaskModel) {
    const title = $localize`:close task header@@closeTaskHeader:Close Task`;
    const msg = $localize`:close confirmation text Task@@closeConfirmationTask:Do you really want to close this task?`;
    const confirmButtonText = $localize`:button text close@@buttonClose:CLOSE`;
    const cancelButtonText = $localize`:button text cancel@@buttonCancel:CANCEL`;
    const dialogData: ConfirmDeleteDialogData = {
      itemName: task.name,
      title: title,
      message: msg,
      confirmText: toTitleCase(confirmButtonText),
      cancelText: toTitleCase(cancelButtonText)
    };

    this.dialogService.openConfirmDelete(dialogData).subscribe((result: boolean) => {
      if (result == true) {
        this.closeTask(task);
      }
    });
  }

  closeTask(task: TaskModel) {
    this.loadingTask = true;
    this.taskService
      .closeTask(task.taskId)
      .catch(() => {
        this.loadingTask = false;
      })
      .then(() => this.taskService.getTask(task.taskId, new HttpParams().append('format', 'FULL')));
  }

  onActionButtonClick(buttonText: string) {
    if (buttonText === REFRESH_CALL_BACK_IDENTIFIER) {
      this.refreshTask();
    }

    if (buttonText === DELETE_CALL_BACK_IDENTIFIER) {
      this.deleteTask(this.task);
    }

    if (buttonText === CLOSE_TASK_CALL_BACK_IDENTIFIER) {
      this.confirmCloseTask(this.task);
    }

    if (buttonText === DISMISS_CALL_BACK_IDENTIFIER) {
      this.onDismissCallback.next();
    }
  }

  getTaskEndTimeStamp(): string | undefined {
    if (this.task.time_tracking) {
      const endItem = this.task.time_tracking.find((item) => item.name === 'end');
      return endItem ? endItem.start?.toString() : undefined;
    }
    return undefined;
  }
}

export class TaskStateLocationData {
  constructor(
    public type: WorkflowItemType,
    public name: string,
    public icon: IconDefinition,
    public location?: LocationModel,
    public timestamp?: string
  ) {}
}
